import { useConflictDetection } from "@/hooks/useConflictDetection.ts";
import type { ArtifactComponent } from "@/types/artifacts-view.ts";
import { AlertCircle, CheckCircle } from "lucide-react";

type ConflictCheckComponentData = Extract<ArtifactComponent, { type: "conflictCheck" }>;

/**
 * Live scheduling-conflict check for an inbound invitation. Runs the same
 * conflict query the legacy card used and lists the actual overlapping events,
 * so the card shows *what* it conflicts with — not just that it conflicts.
 * When the component carries a pre-filled `conflicts` array (server-provided or
 * for the preview), it renders that and skips the live query.
 */
export const ConflictCheckBlock = ({ component }: { component: ConflictCheckComponentData }) => {
  const hasProvided = component.conflicts != null;
  // Prefer the account's effective zone (server-provided) so all-day event
  // boundaries are evaluated consistently with date rendering (BIG-4825);
  // fall back to the device zone.
  const userTimezone = component.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  const live = useConflictDetection({
    startDateMs: component.startMs,
    endDateMs: component.endMs,
    isAllDay: component.isAllDay,
    externalEventId: component.externalEventId,
    userTimezone,
    enabled: !hasProvided,
  });

  const conflicts = hasProvided ? component.conflicts! : live.conflicts;
  const status = hasProvided ? (conflicts.length > 0 ? "conflict" : "clear") : live.status;

  if (status === "loading") return null;

  if (status === "clear") {
    return (
      <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-green-700">
        <CheckCircle className="size-4 shrink-0" />
        <span className="text-sm">No scheduling conflicts</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1 rounded-md bg-red-50 px-3 py-2 text-red-700">
      <div className="flex items-center gap-2">
        <AlertCircle className="size-4 shrink-0" />
        <span className="text-sm font-medium">Conflicts with:</span>
      </div>
      {conflicts.map((conflict, i) => (
        <div key={i} className="ml-6 text-sm">
          <span className="font-medium">{conflict.summary}</span>
          <span className="text-red-600"> · {conflict.formattedRange}</span>
          {conflict.location ? <span className="text-red-500"> · {conflict.location}</span> : null}
        </div>
      ))}
    </div>
  );
};
